import { useRef } from 'preact/hooks'
import { text } from '#contract'
import type { TaskView } from '#contract'
import { useMediaQuery } from '../app/use-media-query.ts'
import { useView } from '../app/use-view.ts'
import { etaText } from '../i18n/eta.ts'
import { useT } from '../i18n/index.ts'
import { isTouchPlaying } from '../touch/touch-controls.tsx'
import styles from './task-list.module.css'
import { useLeaving } from './use-leaving.ts'

const COMPACT = '(max-width: 599px), (max-height: 499px)'
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
/** `--duration-normal`: how long a finished row takes to fade out. */
const LEAVE_MS = 200

/**
 * What the player is working on or waiting for, top left (`view.tasks`, missions first with
 * the accent bar): up to 4 rows (2 on compact), then "+N more". A row is its title, the ETA
 * on the right and an optional detail under it. New rows slide in and finished ones fade out,
 * except the rows already there when the HUD shows. On touch it sits right of Pause and the
 * phone button.
 */
export function TaskList() {
  const t = useT()
  const tasks = useView((view) => view.tasks)
  const touch = useView(isTouchPlaying)
  const reducedSetting = useView((view) => view.settings.accessibility.reducedMotion)
  const reduced = useMediaQuery(REDUCED_MOTION) || reducedSetting
  const max = useMediaQuery(COMPACT) ? 2 : 4
  const rows = useLeaving(tasks.slice(0, max), reduced ? 0 : LEAVE_MS)
  // Rows there when the HUD shows (again, after a pause) don't slide in.
  const initial = useRef<ReadonlySet<string> | null>(null)
  initial.current ??= new Set(tasks.map((task) => task.id))
  const there = initial.current
  if (rows.length === 0) return null

  const more = tasks.length - Math.min(tasks.length, max)
  return (
    <ul class={styles.list} data-touch={touch || undefined}>
      {rows.map(({ item, leaving }) => (
        <TaskRow key={item.id} task={item} enter={!there.has(item.id)} leaving={leaving} />
      ))}
      {more > 0 && <li class={styles.more}>{t(text('tasks.more', { n: more }))}</li>}
    </ul>
  )
}

interface TaskRowProps {
  readonly task: TaskView
  readonly enter: boolean
  readonly leaving: boolean
}

function TaskRow({ task, enter, leaving }: TaskRowProps) {
  const t = useT()
  return (
    <li
      class={styles.row}
      data-kind={task.kind}
      data-enter={enter || undefined}
      data-leaving={leaving || undefined}
    >
      <span class={styles.title}>{t(task.title)}</span>
      {task.eta && <span class={styles.eta}>{t(etaText(task.eta.minutes))}</span>}
      {task.detail && <span class={styles.detail}>{t(task.detail)}</span>}
    </li>
  )
}
